'use client';
import { memo as Memo, useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

//* Helpers
import breakString from '@helpers/breakString';

//* Components
import Text from '../Text';
import LazySection from '../LazySection';
import TestimonialContent from './TestimonialContent';
import HTMLContainer from '@components/global/HTMLContainer';
import TestimonialContentSkeleton from '@/components/skeleton/TestimonialContentSkeleton';

//* Styles
import TestimonialsStyle from './style';
import 'swiper/css';

const Testimonials = Memo(({ title, dark, items }) => {
	//! Refs
	const sectionRef = useRef(null);
	const sliderParentContRef = useRef(null);
	const titleRef = useRef(null);

	//! Animation (ScrollTrigger)
	useLayoutEffect(() => {
		if (!sectionRef.current || !titleRef.current || !sliderParentContRef.current) return;

		const ctx = gsap.context(() => {
			const titles = Array.from(titleRef.current.children);
			const special = titleRef.current.children[1];
			const slides = Array.from(sliderParentContRef.current.children);

			gsap.set(slides, { opacity: 0 });
			gsap.set(titles, { opacity: 0 });
			if (special) gsap.set(special, { fontStyle: 'normal', fontWeight: 400 });

			const tl = gsap.timeline({ paused: true, defaults: { ease: 'none', overwrite: 'auto' } });

			if (special) tl.set(special, { fontStyle: 'italic', fontWeight: 300 }, 0.4);
			tl.to(slides, { opacity: 1, duration: 0.2 }, 0.13);
			tl.to(titles, { opacity: 1, duration: 0.001, stagger: 0.2 }, 0);

			const hide = () => {
				gsap.set(titles, { opacity: 0 });
				gsap.to(slides, { opacity: 0, duration: 0.2, overwrite: 'auto' });
				if (special) gsap.set(special, { fontStyle: 'normal', fontWeight: 400 });
				tl.pause(0);
			};

			ScrollTrigger.create({
				trigger: titleRef.current,
				start: 'top bottom',
				end: 'bottom top',
				// markers: true,
				onEnter: () => tl.play(0),
				onLeaveBack: () => hide(),
			});
		}, sectionRef);

		const ro = new ResizeObserver(() => requestAnimationFrame(() => ScrollTrigger.refresh()));
		ro.observe(document.body);

		return () => {
			ro.disconnect();
			ctx.revert();
		};
	}, []);

	return (
		<TestimonialsStyle
			ref={sectionRef}
			className={`testimonialsContainer ${dark ? 'darkContainer' : ''}`}>
			<HTMLContainer>
				<Text
					tag='h2'
					ref={titleRef}
					className='h2 font-poppins font-semi-bold title'>
					{breakString(title)}
				</Text>

				<div
					ref={sliderParentContRef}
					className='slideParentCont'>
					<LazySection
						delay={500}
						fallback={<TestimonialContentSkeleton />}>
						<TestimonialContent items={items} />
					</LazySection>
				</div>
			</HTMLContainer>
		</TestimonialsStyle>
	);
});

export default Testimonials;
